import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Container, Text, Sprite, Graphics } from "@pixi/react";
import * as PIXI from "pixi.js";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import anime from "animejs";
import { createButtonPressAnimation } from "../../../../utilities";
import { closeErrorPopup, setUserInterfaceDisabled, setSpinComplete, setErrorRecoveryTransitionActive, setReelsFrozen } from "../../../../State/actions";
import { selectErrorPopupActive, selectErrorPopupCode, selectReelsFrozen } from "../../../../State/selectors";
import { refreshGameData } from "../../../../State/Slices/gameSettingsSlice";
import { setShouldDeductNextSpin } from "../../../../State/Slices/freeRoundsSlice";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { ASSET_NAME } from "../../../../assets";
import { FONT_CONFIG, TEXT_STYLE_CONFIG } from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

// Development mode - set to true to test popup with long message
const DEV_MODE = false;

// Overlay settings
const OVERLAY_BACKGROUND_ALPHA = 0.6;
const OVERLAY_BACKGROUND_COLOR = 0x000000;
const OVERLAY_FADE_DURATION = 300;

// Popup animation settings (same as InfoPopup)
const POPUP_SCALE_DURATION = 400;
const POPUP_SCALE_EASING = "easeOutBack";
const POPUP_SCALE_DELAY = 150;

// Popup panel settings
const PANEL_BACKGROUND_COLOR = 0x000000;
const PANEL_BACKGROUND_ALPHA = 0.8;

// Error popup configuration - Unified scaling system (like BuyBonus)
const ERROR_POPUP_CONFIG = {
  // Reference width for base values (all base values are defined at this width)
  REFERENCE_WIDTH: 600,

  // Base values at reference width (600px)
  basePadding: 40,
  baseMessageFontSize: 24,
  baseButtonFontSize: 24,
  baseTextButtonGap: 40,
  baseBorderRadius: 20,
  baseMinEdgePadding: 20, // Minimum gap from screen edges
  baseContentWidth: 480, // Narrower content width for better text layout (600 - 80*2 margin)

  // Device-specific scale multipliers (ONLY scale, everything else follows)
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        scaleMultiplier: 0.6, // Mobile portrait - keep full size
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        scaleMultiplier: 0.55, // Mobile landscape - keep full size
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        scaleMultiplier: 0.7, // Tablet portrait - 70% size
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        scaleMultiplier: 0.75, // Tablet landscape - 60% size
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        scaleMultiplier: 1.0, // Desktop - 50% size
      },
    },
  },
};

const WarningPopup = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { dimensions, device, orientation } = useDimensions();
  const errorPopupActive = useSelector(selectErrorPopupActive);
  const errorCode = useSelector(selectErrorPopupCode);
  const reelsFrozen = useSelector(selectReelsFrozen);

  // Extract deposit_url from window options
  const depositUrl = window.__OPTIONS__?.deposit_url;


  const isOpen = DEV_MODE ? true : errorPopupActive; // Force open in dev mode

  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const popupContentRef = useRef(null);

  // Refs for button sprites and animation state
  const okButtonSpriteRef = useRef(null);
  const depositButtonSpriteRef = useRef(null);
  const isOkPressAnimatingRef = useRef(false);
  const isDepositPressAnimatingRef = useRef(false);

  // Create brightness filters for hover effects
  const okBrightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  okBrightnessFilter.current.brightness(2.0, false);

  const depositBrightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  depositBrightnessFilter.current.brightness(2.0, false);

  // Use refs for hover state to avoid unnecessary re-renders
  const isOkHoveredRef = useRef(false);
  const isDepositHoveredRef = useRef(false);


  // Use error code directly as translation key
  // For error 301: use "301nodep" if no deposit_url, otherwise use "301"
  // For errors without specific translations (600, 700, etc): use "default" with error code
  const translationKey = useMemo(() => {
    if (errorCode === 301) {
      return depositUrl ? "301" : "301nodep";
    }
    return String(errorCode);
  }, [errorCode, depositUrl]);

  // Check if translation exists for this error code, otherwise use default
  const errorMessage = useMemo(() => {
    if (!errorCode) {
      return t("default", { code: "unknown" });
    }

    // Try to get the translation
    const translatedMessage = t(translationKey);

    // If translation key equals the returned message, it means no translation exists
    // (i18n returns the key itself when translation is missing)
    if (translatedMessage === translationKey) {
      // Use default message with error code injected
      return t("default", { code: errorCode });
    }

    return translatedMessage;
  }, [errorCode, translationKey, t]);

  // Memoize device config
  const deviceConfigMemo = useMemo(() => {
    return ERROR_POPUP_CONFIG.DEVICE_CONFIGS[device]?.[orientation] ||
           ERROR_POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP][PixiSettings.ORIENTATION.LANDSCAPE];
  }, [device, orientation]);

  const centerX = useMemo(() => dimensions.width / 2, [dimensions.width]);
  const centerY = useMemo(() => dimensions.height / 2, [dimensions.height]);

  // Get button texture
  const buttonTexture = useMemo(
    () => PIXI.Assets.get(ASSET_NAME.button_bet_background),
    []
  );

  // Calculate popup scale: just device multiplier (no max width constraints)
  const popupScale = useMemo(
    () => deviceConfigMemo.scaleMultiplier || 1.0,
    [deviceConfigMemo]
  );

  // Apply popup scale to ALL base values (single source of truth)
  const padding = useMemo(
    () => ERROR_POPUP_CONFIG.basePadding * popupScale,
    [popupScale]
  );
  const messageFontSize = useMemo(
    () => Math.round(ERROR_POPUP_CONFIG.baseMessageFontSize * popupScale),
    [popupScale]
  );
  const buttonFontSize = useMemo(
    () => Math.round(ERROR_POPUP_CONFIG.baseButtonFontSize * popupScale),
    [popupScale]
  );
  const textButtonGap = useMemo(
    () => ERROR_POPUP_CONFIG.baseTextButtonGap * popupScale,
    [popupScale]
  );
  const borderRadius = useMemo(
    () => ERROR_POPUP_CONFIG.baseBorderRadius * popupScale,
    [popupScale]
  );

  // Fixed content width for consistent text wrapping across all devices
  const contentWidth = useMemo(
    () => ERROR_POPUP_CONFIG.baseContentWidth * popupScale,
    [popupScale]
  );

  // Text styling - MUST be before early return
  const messageStyle = useMemo(() => new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: messageFontSize,
    fontWeight: "700",
    fill: 0xffffff,
    align: "center",
    wordWrap: true,
    wordWrapWidth: contentWidth, // Fixed width for consistent wrapping
    ...TEXT_STYLE_CONFIG.OUTLINE,
  }), [messageFontSize, contentWidth]);

  // Button text styling - MUST be before early return
  const buttonTextStyle = useMemo(() => new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: buttonFontSize,
    fontWeight: "700",
    fill: 0xffffff,
    align: "center",
    ...TEXT_STYLE_CONFIG.OUTLINE,
  }), [buttonFontSize]);

  // Calculate actual text height using TextMetrics - MUST be before early return
  const textMetrics = useMemo(() => {
    return TextMetrics.measureText(errorMessage, messageStyle);
  }, [errorMessage, messageStyle]);

  // Button dimensions (scales with popup)
  const buttonWidth = useMemo(
    () => buttonTexture.width * popupScale,
    [buttonTexture.width, popupScale]
  );
  const buttonHeight = useMemo(
    () => buttonTexture.height * popupScale,
    [buttonTexture.height, popupScale]
  );

  // Calculate dynamic positions
  const textHeight = useMemo(() => textMetrics.height, [textMetrics.height]);

  // Calculate panel dimensions (fixed width, dynamic height based on text)
  const topPaddingMultiplier = 1.5; // Extra space at top for exclamation mark
  const panelWidth = useMemo(
    () => contentWidth + padding * 2,
    [contentWidth, padding]
  );
  const panelHeight = useMemo(
    () => textHeight + textButtonGap + buttonHeight + padding * (1 + topPaddingMultiplier),
    [textHeight, textButtonGap, buttonHeight, padding]
  );

  // Note: panelHeight is dynamic - it grows/shrinks based on text length
  // If you want fixed height too, set a baseHeight and use: panelHeight = baseHeight * popupScale

  // Center the panel perfectly in the middle of the screen
  const panelX = useMemo(
    () => centerX - panelWidth / 2,
    [centerX, panelWidth]
  );
  const panelY = useMemo(
    () => centerY - panelHeight / 2,
    [centerY, panelHeight]
  );

  // Position content - add extra top padding for exclamation mark
  const messageY = useMemo(
    () => panelY + padding * topPaddingMultiplier + textHeight / 2,
    [panelY, padding, textHeight]
  );
  const buttonY = useMemo(
    () => panelY + padding * topPaddingMultiplier + textHeight + textButtonGap + buttonHeight / 2,
    [panelY, padding, textHeight, textButtonGap, buttonHeight]
  );

  // Exclamation mark position (top left corner with padding offset)
  const exclamationX = useMemo(
    () => panelX + padding * 0.5,
    [panelX, padding]
  );
  const exclamationY = useMemo(
    () => panelY + padding * 0.5,
    [panelY, padding]
  );
  const exclamationFontSize = useMemo(
    () => Math.round(messageFontSize * 1.3),
    [messageFontSize]
  );

  // Memoize exclamation mark text style
  const exclamationStyle = useMemo(() => new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: exclamationFontSize,
    fontWeight: "700",
    fill: 0xffffff,
    align: "center",
    ...TEXT_STYLE_CONFIG.OUTLINE,
  }), [exclamationFontSize]);

  // Memoize translation texts
  const okText = useMemo(() => t("ok"), [t]);
  const depositText = useMemo(() => t("deposit"), [t]);

  // Memoize button positions for two-button layout
  const okButtonX = useMemo(
    () => centerX - buttonWidth / 2 - padding * 0.5,
    [centerX, buttonWidth, padding]
  );
  const depositButtonX = useMemo(
    () => centerX + buttonWidth / 2 + padding * 0.5,
    [centerX, buttonWidth, padding]
  );

  // Memoize Graphics draw functions
  const drawOverlay = useCallback((g) => {
    g.clear();
    g.beginFill(OVERLAY_BACKGROUND_COLOR);
    g.drawRect(0, 0, dimensions.width, dimensions.height);
    g.endFill();
  }, [dimensions.width, dimensions.height]);

  const drawPanel = useCallback((g) => {
    g.clear();
    g.beginFill(PANEL_BACKGROUND_COLOR, PANEL_BACKGROUND_ALPHA);
    g.drawRoundedRect(
      panelX,
      panelY,
      panelWidth,
      panelHeight,
      borderRadius
    );
    g.endFill();
  }, [panelX, panelY, panelWidth, panelHeight, borderRadius]);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
  }, [isClosing]);

  // Hover effect handlers for OK button
  const handleOkPointerOver = useCallback(() => {
    if (okButtonSpriteRef.current) {
      isOkHoveredRef.current = true;
      if (
        !okButtonSpriteRef.current.filters ||
        okButtonSpriteRef.current.filters.length === 0
      ) {
        okButtonSpriteRef.current.filters = [okBrightnessFilter.current];
      }
    }
  }, []);

  const handleOkPointerOut = useCallback(() => {
    if (okButtonSpriteRef.current) {
      isOkHoveredRef.current = false;
      okButtonSpriteRef.current.filters = [];
    }
  }, []);

  // Hover effect handlers for Deposit button
  const handleDepositPointerOver = useCallback(() => {
    if (depositButtonSpriteRef.current) {
      isDepositHoveredRef.current = true;
      if (
        !depositButtonSpriteRef.current.filters ||
        depositButtonSpriteRef.current.filters.length === 0
      ) {
        depositButtonSpriteRef.current.filters = [depositBrightnessFilter.current];
      }
    }
  }, []);

  const handleDepositPointerOut = useCallback(() => {
    if (depositButtonSpriteRef.current) {
      isDepositHoveredRef.current = false;
      depositButtonSpriteRef.current.filters = [];
    }
  }, []);

  // OK button click handler with press animation
  const handleOkClick = useCallback((e) => {
    e.stopPropagation();
    
    // Play click sound
    soundManager.play(SOUND_NAME.button_click);
    
    // Add press animation before handling click
    if (okButtonSpriteRef.current && !isOkPressAnimatingRef.current) {
      isOkPressAnimatingRef.current = true;
      createButtonPressAnimation(okButtonSpriteRef.current, {
        targetWidth: buttonWidth,
        targetHeight: buttonHeight,
        onComplete: () => {
          isOkPressAnimatingRef.current = false;
          // Close popup after animation completes
          handleClose();
        }
      });
    } else {
      // If animation is already running or sprite not ready, close immediately
      handleClose();
    }
  }, [buttonWidth, buttonHeight, handleClose]);

  // Deposit button click handler with press animation
  const handleDepositClick = useCallback((e) => {
    e.stopPropagation();
    
    // Play click sound
    soundManager.play(SOUND_NAME.button_click);
    
    // Add press animation before handling click
    if (depositButtonSpriteRef.current && !isDepositPressAnimatingRef.current) {
      isDepositPressAnimatingRef.current = true;
      createButtonPressAnimation(depositButtonSpriteRef.current, {
        targetWidth: buttonWidth,
        targetHeight: buttonHeight,
        onComplete: () => {
          isDepositPressAnimatingRef.current = false;
          // Redirect after animation completes
          if (depositUrl) {
            window.location.href = depositUrl;
          }
        }
      });
    } else {
      // If animation is already running or sprite not ready, redirect immediately
      if (depositUrl) {
        window.location.href = depositUrl;
      }
    }
  }, [buttonWidth, buttonHeight, depositUrl]);

  // Handle overlay fade in, popup scale up, and UI disable when opening (same as InfoPopup)
  useEffect(() => {
    if (isOpen && !isClosing) {
      // Disable UI when popup opens
      dispatch(setUserInterfaceDisabled(true));

      // 1. Fade in overlay
      anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
      });

      // 2. Scale up popup (with slight delay, starts during overlay fade)
      if (popupContentRef.current) {
        anime({
          targets: popupContentRef.current.scale,
          x: [0, 1],
          y: [0, 1],
          duration: POPUP_SCALE_DURATION,
          delay: POPUP_SCALE_DELAY,
          easing: POPUP_SCALE_EASING,
        });
      }
    } else if (!isOpen && !isClosing) {
      // Re-enable UI when popup closed externally (no animation needed)
      dispatch(setUserInterfaceDisabled(false));
    }
  }, [isOpen, isClosing, dispatch]);

  // Handle close animation when user clicks OK - same as InfoPopup (scale down popup, fade overlay)
  useEffect(() => {
    if (!isClosing) return;

    let overlayAnim = null;
    let popupAnim = null;

    // 1. Scale down popup (reverse of scale up)
    if (popupContentRef.current) {
      popupAnim = anime({
        targets: popupContentRef.current.scale,
        x: 0,
        y: 0,
        duration: POPUP_SCALE_DURATION,
        easing: "easeInBack",
      });
    }

    // 2. Fade out overlay (can run in parallel)
    overlayAnim = anime({
      targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
      alpha: 0,
      duration: OVERLAY_FADE_DURATION,
      delay: 50,
      easing: "easeInQuad",
      update: (anim) => {
        setOverlayAlpha(anim.animations[0].currentValue);
      },
      complete: () => {
        setOverlayAlpha(0);
        dispatch(setUserInterfaceDisabled(false));
        dispatch(closeErrorPopup());
        dispatch(setSpinComplete(true));

        // ONLY trigger error recovery if reels were actually frozen
        if (reelsFrozen) {
          dispatch(setErrorRecoveryTransitionActive(true));
          dispatch(setReelsFrozen(false));
        }

        // Refresh game data ONLY for error 205 (free rounds activation)
        if (errorCode === 205) {
          dispatch(refreshGameData({
            useApiData: reelsFrozen,
            ignoreFreeRoundsStorage: true,
          }));
        }

        // Set flag to force bet deduction on next spin for error 206 (free rounds ended)
        if (errorCode === 206) {
          dispatch(setShouldDeductNextSpin(true));
        }

        setIsClosing(false);
      },
    });

    return () => {
      if (overlayAnim) overlayAnim.pause();
      if (popupAnim) popupAnim.pause();
    };
  }, [isClosing, dispatch, reelsFrozen, errorCode]);

  // Clean up hover effects on unmount
  useEffect(() => {
    const okSprite = okButtonSpriteRef.current;
    const depositSprite = depositButtonSpriteRef.current;
    return () => {
      if (okSprite) {
        okSprite.filters = [];
      }
      if (depositSprite) {
        depositSprite.filters = [];
      }
    };
  }, []);

  // Early return AFTER all hooks - stay mounted during close animation
  if (!isOpen && !isClosing) {
    return null;
  }

  return (
    <Container name="warning_popup" zIndex={10000} sortableChildren={true}>
      {/* Black overlay - NOT clickable, more transparent */}
      <Graphics
        draw={drawOverlay}
        alpha={overlayAlpha}
        eventMode="static"
        pointerdown={(e) => {
          e.stopPropagation();
          // Do nothing - user cannot close by clicking overlay
        }}
        zIndex={10000}
      />

      {/* Content container - animated scale (same structure as InfoPopup) */}
      <Container
        name="popup_content"
        ref={popupContentRef}
        zIndex={10001}
        x={centerX}
        y={centerY}
        pivot={{ x: centerX, y: centerY }}
      >
        {/* Dark panel background - wraps text and button */}
        <Graphics
          draw={drawPanel}
        />

        {/* Exclamation mark - top left corner */}
        <Text
          text="!"
          style={exclamationStyle}
          anchor={{ x: 0, y: 0 }}
          x={exclamationX}
          y={exclamationY}
          resolution={2}
        />

        {/* Error message - dynamically positioned based on text height */}
        <Text
          text={errorMessage}
          style={messageStyle}
          anchor={0.5}
          x={centerX}
          y={messageY}
          resolution={2}
        />

        {/* Buttons - single OK button or two buttons for error 301 with deposit */}
        {errorCode === 301 && depositUrl ? (
          // Two buttons side by side for error 301 with deposit URL
          <>
            {/* OK Button - left side */}
            <Container
              name="ok_button"
              x={okButtonX}
              y={buttonY}
            >
              {/* Interactive area - handles all pointer events */}
              <Graphics
                name="ok_button_interactive_area"
                eventMode="static"
                cursor="pointer"
                hitArea={new PIXI.Rectangle(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight)}
                onpointertap={handleOkClick}
                onpointerover={handleOkPointerOver}
                onpointerout={handleOkPointerOut}
                draw={(g) => {
                  g.clear();
                  // Nearly invisible fill (required for hit detection to work)
                  g.beginFill(0x000000, 0.01);
                  g.drawRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight);
                  g.endFill();
                }}
              />
              
              <Sprite
                ref={okButtonSpriteRef}
                texture={buttonTexture}
                anchor={0.5}
                width={buttonWidth}
                height={buttonHeight}
                eventMode="none"
              />
              <Text
                text={okText}
                style={buttonTextStyle}
                anchor={0.5}
                resolution={2}
                eventMode="none"
              />
            </Container>

            {/* Deposit Button - right side */}
            <Container
              name="deposit_button"
              x={depositButtonX}
              y={buttonY}
            >
              {/* Interactive area - handles all pointer events */}
              <Graphics
                name="deposit_button_interactive_area"
                eventMode="static"
                cursor="pointer"
                hitArea={new PIXI.Rectangle(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight)}
                onpointertap={handleDepositClick}
                onpointerover={handleDepositPointerOver}
                onpointerout={handleDepositPointerOut}
                draw={(g) => {
                  g.clear();
                  // Nearly invisible fill (required for hit detection to work)
                  g.beginFill(0x000000, 0.01);
                  g.drawRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight);
                  g.endFill();
                }}
              />
              
              <Sprite
                ref={depositButtonSpriteRef}
                texture={buttonTexture}
                anchor={0.5}
                width={buttonWidth}
                height={buttonHeight}
                eventMode="none"
              />
              <Text
                text={depositText}
                style={buttonTextStyle}
                anchor={0.5}
                resolution={2}
                eventMode="none"
              />
            </Container>
          </>
        ) : (
          // Single OK button for all other cases
          <Container
            name="ok_button"
            x={centerX}
            y={buttonY}
          >
            {/* Interactive area - handles all pointer events */}
            <Graphics
              name="ok_button_interactive_area"
              eventMode="static"
              cursor="pointer"
              hitArea={new PIXI.Rectangle(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight)}
              onpointertap={handleOkClick}
              onpointerover={handleOkPointerOver}
              onpointerout={handleOkPointerOut}
              draw={(g) => {
                g.clear();
                // Nearly invisible fill (required for hit detection to work)
                g.beginFill(0x000000, 0.01);
                g.drawRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight);
                g.endFill();
              }}
            />
            
            <Sprite
              ref={okButtonSpriteRef}
              texture={buttonTexture}
              anchor={0.5}
              width={buttonWidth}
              height={buttonHeight}
              eventMode="none"
            />
            <Text
              text={okText}
              style={buttonTextStyle}
              anchor={0.5}
              resolution={2}
              eventMode="none"
            />
          </Container>
        )}
      </Container>
    </Container>
  );
};

export default WarningPopup;
